export interface MergeMapStrategy<Item> {
    /**
     * Compare two items.
     * Should return 0 if the items are equal.
     */
    compareItems(a: Item, b: Item): number;

    /**
     * Merge two conflicting items.
     */
    mergeItems: (mergeCase: { ancestor: Item | undefined; base: Item; head: Item }) => Item;
}

/**
 * 3-way merge on maps.
 * The current implementation doesn't support conflicts and is optimized to avoid loss of data.
 */
export function mergeMaps<Item>(
    maps: {
        ancestor: Record<string, Item>;
        base: Record<string, Item>;
        head: Record<string, Item>;
    },
    strategy: MergeMapStrategy<Item>
): Record<string, Item> {
    const ancestorKeys = Object.keys(maps.ancestor);
    const baseKeys = Object.keys(maps.base);
    const headKeys = Object.keys(maps.head);

    const allKeys = new Set<string>(ancestorKeys.concat(baseKeys, headKeys));

    const result: Record<string, Item> = {};

    allKeys.forEach((key) => {
        const ancestor = maps.ancestor[key];
        const base = maps.base[key];
        const head = maps.head[key];

        const headModified = head && ancestor ? strategy.compareItems(ancestor, head) !== 0 : false;
        const baseModified = base && ancestor ? strategy.compareItems(ancestor, base) !== 0 : false;

        if (base && !head) {
            if (!ancestor || baseModified) {
                // Base modified the item, We preserve it
                result[key] = base;
            } else {
                // Head deleted the item
            }
        } else if (!base && head) {
            if (!ancestor || headModified) {
                // Head modified the item, We preserve it
                result[key] = head;
            } else {
                // Base deleted the item
            }
        } else if (head && base) {
            if (headModified && baseModified) {
                // Both side modified the item
                result[key] = strategy.mergeItems({ ancestor, base, head });
            } else if (headModified) {
                // Head modified the item
                result[key] = head;
            } else if (baseModified) {
                // Base modified the item
                result[key] = base;
            } else {
                // Both side did not modify the item
                result[key] = base;
            }
        } else {
            // Both side deleted the item
        }
    });

    return result;
}
